import React, { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Select,
  Input,
  Card,
  Row,
  Col,
  Modal,
  message,
  Space,
  DatePicker,
  Typography,
  Progress,
  Tooltip
} from 'antd';
import { DownloadOutlined, FilterOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { URL_API } from '@configs/';
import './style.css';
import { showError } from '@components/notification';
import { communityList, convertLocaltimeToUTC, formatCurrency } from '@utils/utils';
import {
  apiGetOriginalData,
  apiSyncData,
  apiSyncTimeData
} from '@services/ManageDevices/DeviceServices';
import { loadFromLocalStorageObjectFromBase64, saveToLocalStorage } from '@databases/localStorage';
import { contantAuthentication } from '@contants/';
import { get, isEmpty, isNumber } from 'lodash';
import { SelectCommunity } from '@page/Common/SelectCommunity';
import {
  apiGetStatisticsCustomerByDay,
  apiGetStatisticsData
} from '@services/ManageCustomers/CustomerServices';
import { apiGetFavoriteFilters } from '@services/ManageCustomers/FavoriteFilterServices';
import { ADMIN_ROLE } from '@utils/constant';
import Chart from '@components/charts/Chart';
import ChartCard from '@components/charts/ChartCard';
import { consumptionWithMeterOptions } from '@components/charts/chartPresets';
import { COLORS } from '@components/charts/chart.constant';
import dayjs from 'dayjs';
import { usePermission } from '@contexts/Auth/UsePermission';
import { Link } from 'react-router-dom';
import FilterAdvanceDrawer from '@components/FilterAdvance';
import ConsumptionCard from './ConsumptionCard';

const { Option } = Select;

const { RangePicker } = DatePicker;

const METER_REPLACEMENT_TYPES = ['REPLACE_METER', 'REPLACE_ALL'];
const NEW_INSTALLATION_TYPE = 'NEW';

const buildMeterReadingSeries = (row, hourlyConsumption) => {
  const startReading = Number(row?.start_meter_reading_at_day) || 0;
  const meterReplacementType = row?.meter_replacement_type || row?.type;
  const meterReplacementDate = row?.meter_replacement_date || row?.replacement_date;
  const meterReadingNew = Number(row?.meter_reading_new_at_replacement ?? row?.meter_reading_new);
  const replacementHour = meterReplacementDate ? moment.utc(meterReplacementDate).hour() : null;
  const shouldResetToNewMeter =
    METER_REPLACEMENT_TYPES.includes(meterReplacementType) &&
    Number.isFinite(meterReadingNew) &&
    replacementHour !== null &&
    replacementHour >= 0;
  const statisticsDate = moment.utc(
    row?.denseData_startCollectionTime,
    ['DD-MM-YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'],
    true
  );
  const installationDate = row?.start_date ? moment.utc(row.start_date) : null;
  const isNewInstallationDay =
    meterReplacementType === NEW_INSTALLATION_TYPE &&
    statisticsDate.isValid() &&
    installationDate?.isValid() &&
    installationDate.isSame(statisticsDate, 'day');
  const installationHour = isNewInstallationDay ? installationDate.hour() : null;
  const installationReading = Number.isFinite(meterReadingNew) ? meterReadingNew : 0;

  const cumulativeReading = [];
  let accumulator = isNewInstallationDay ? installationReading : startReading;

  for (let i = 0; i < hourlyConsumption.length; i++) {
    if (isNewInstallationDay && i < installationHour) {
      // null để biểu đồ không vẽ đường trước thời điểm thiết bị được lắp đặt.
      cumulativeReading.push(null);
      continue;
    }

    if (shouldResetToNewMeter && i === replacementHour) {
      accumulator = meterReadingNew;
    }

    cumulativeReading.push(accumulator);
    accumulator += hourlyConsumption[i];
  }

  return cumulativeReading;
};

export default function List() {
  const defaultStatisticsDate = dayjs().subtract(1, 'day');
  const [data, setData] = useState([]);
  const [flagSearch, setFlagSearch] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCommunity, setSelectedCommunity] = useState('HP');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [rangeDateString, setRangeDateString] = useState([
    defaultStatisticsDate.format('YYYY-MM-DD'),
    defaultStatisticsDate.format('YYYY-MM-DD')
  ]);
  const [serialNo, setSerialNo] = useState(null);
  const [loadingFetch, setLoadingFetch] = useState(false);
  const user = JSON.parse(loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH));
  const [selectedRowKey, setSelectedRowKey] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedDate, setSelectedDate] = useState(defaultStatisticsDate);
  const [exportDateRange, setExportDateRange] = useState([
    defaultStatisticsDate,
    defaultStatisticsDate
  ]);
  const [exportRangeDateString, setExportRangeDateString] = useState([
    defaultStatisticsDate.format('YYYY-MM-DD'),
    defaultStatisticsDate.format('YYYY-MM-DD')
  ]);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { dailyConsumption } = usePermission();
  const [isOpen, setIsOpen] = useState(false);
  const [dataFilterAdvance, setDataFilterAdvance] = useState({});
  const [favoriteFilters, setFavoriteFilters] = useState([]);
  const [selectedFavoriteFilter, setSelectedFavoriteFilter] = useState(null);
  const [loadingFavoriteFilters, setLoadingFavoriteFilters] = useState(false);
  const openDrawer = () => {
    setIsOpen(true);
  };

  const onDrawerClose = () => {
    setIsOpen(false);
  };

  // Fetch favorite customer filters
  const fetchFavoriteFilters = async () => {
    try {
      setLoadingFavoriteFilters(true);
      const res = await apiGetFavoriteFilters();
      const customerFilters = (res.data?.data || []).filter((f) => f.filter_type === 'customer');
      setFavoriteFilters(
        customerFilters.map((f) => ({
          label: `${f.filter_name} (${f.item_ids?.length || 0} KH)`,
          value: f.id,
          item_ids: f.item_ids
        }))
      );
    } catch (err) {
      console.error('Error fetching favorite filters:', err);
    } finally {
      setLoadingFavoriteFilters(false);
    }
  };

  useEffect(() => {
    fetchFavoriteFilters();
  }, []);
  const fetchData = async () => {
    try {
      setSelectedRowKey(null);
      setSelectedRow(null);
      setLoadingFetch(true);
      const res = await apiGetStatisticsCustomerByDay({
        page: currentPage,
        size: pageSize,
        keyWord: search?.trim() ?? undefined,
        serialNo: serialNo,
        community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
        fromDate: rangeDateString?.length ? rangeDateString[0] : undefined,
        toDate: rangeDateString?.length ? rangeDateString[1] : undefined,
        listCust: selectedFavoriteFilter
          ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter)?.item_ids?.join(',')
          : undefined,
        ...dataFilterAdvance
      });
      setData(
        res.data?.data?.map((item, index) => ({
          ...item,
          stt: (currentPage - 1) * pageSize + 1 + index
        }))
      );
      setTotalItems(res.data?.meta?.total);
    } catch (err) {
      showError({ message: 'Something went wrong!' });
      console.log(err);
    } finally {
      setLoadingFetch(false);
    }
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleChangePageSize = (current, size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const onChangeDate = (date, dateString) => {
    setSelectedDate(date);
    setRangeDateString(dateString ? [dateString, dateString] : []);
  };

  const onChangeExportDateRange = (dates, dateStrings) => {
    setExportDateRange(dates);
    setExportRangeDateString(dateStrings);
  };

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     if (!search && !dateRange?.length && currentPage === 1) {
  //       setCurrentPage(1);
  //       fetchData();
  //     }
  //   }, 50000);

  //   return () => clearInterval(interval);
  // }, [search, selectedCommunity, dateRange?.length, currentPage, custId]);

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize, flagSearch]);

  const columns = [
    {
      title: 'DBĐD',
      dataIndex: 'code',
      fixed: 'left',
      width: 80,
      render: (text, record) => (
        <p style={{ color: 'var(--link)', margin: 0 }}>
          {`${record?.code}
          `}
        </p>
      )
    },
    {
      title: 'Tên khách hàng',
      dataIndex: 'name',
      fixed: 'left',
      width: 180,
      render: (text, record) => (
        <p style={{ color: 'var(--link)', margin: 0 }}>
          {`${record?.name}
          `}
        </p>
      )
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'address',
      width: 200
    },
    {
      title: 'CN',
      dataIndex: 'branch',
      width: 100
    },
    {
      title: 'TQL',
      dataIndex: 'manage_group',
      width: 70
    },
    {
      title: 'DHK',
      dataIndex: 'parent_device',
      width: 70
    },
    {
      title: 'Tuyến',
      dataIndex: 'route',
      width: 80
    },

    {
      title: 'Cỡ ĐH',
      dataIndex: 'size',
      width: 90
    },
    {
      title: 'Thiết bị',
      dataIndex: 'serial_no',
      width: 100,
      render: (text, record) => (
        <p
          style={{
            color: 'var(--link)',
            margin: 0
          }}>{`SN:${record.serial_no}/ IMEI:${record.imei}`}</p>
      )
    },
    {
      title: 'Ngày thống kê',
      dataIndex: 'denseData_startCollectionTime',
      key: 'denseData_startCollectionTime',
      render: (_, record) => (
        <>
          {record?.denseData_startCollectionTime
            ? record?.denseData_startCollectionTime
            : 'Chưa có dữ liệu'}
        </>
      ),

      width: 150
    },
    {
      title: 'Chỉ số tại thời điểm 00:00 (lít)',
      dataIndex: 'start_meter_reading_at_day',
      key: 'start_meter_reading_at_day',
      render: (_, record) => (
        <>
          {isNumber(record?.start_meter_reading_at_day)
            ? formatCurrency(record?.start_meter_reading_at_day)
            : '-'}
        </>
      ),
      width: 150
    },
    {
      title: 'Tiêu thụ trong ngày (lít)',
      dataIndex: 'water_consumption_of_the_day',
      key: 'water_consumption_of_the_day',
      render: (_, record) => (
        <>
          {isNumber(record?.water_consumption_of_the_day)
            ? formatCurrency(record?.water_consumption_of_the_day)
            : '-'}
        </>
      ),
      width: 120
    },
    {
      title: 'Lượng nước tiêu thụ qua các giờ (lít)',
      children: [
        ...Array.from({ length: 24 }, (_, index) => ({
          title: `${index}:00`,
          dataIndex: `hour${index}`,
          key: `hour${index}`,
          width: 80,
          render: (_, record) => (
            <>
              {isNumber(record?.diffirenceOfTheFlowWaterMeter?.[index])
                ? formatCurrency(record?.diffirenceOfTheFlowWaterMeter?.[index])
                : '-'}
            </>
          )
        }))
      ]
    }
  ];

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const handleSerialNoChange = (e) => {
    setSerialNo(e.target.value);
  };

  const [loading, setLoading] = useState(false);
  const handleExportExcelButtonClick = async () => {
    let user = loadFromLocalStorageObjectFromBase64(contantAuthentication.DATA_AUTH);
    if (!isEmpty(user)) {
      user = JSON.parse(user);
    }
    if (!exportDateRange || !exportDateRange[0] || !exportDateRange[1]) {
      message.error('Vui lòng chọn khoảng thời gian xuất báo cáo!');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.get(
        `${URL_API}/api/customer/v2/statistics-customer-by-day/export`,
        {
          params: {
            keyWord: search?.trim() ?? undefined,
            serialNo: serialNo,
            community: selectedCommunity === 'ALL' ? undefined : selectedCommunity,
            fromDate: exportRangeDateString?.length ? exportRangeDateString[0] : undefined,
            toDate: exportRangeDateString?.length ? exportRangeDateString[1] : undefined,
            listCust: selectedFavoriteFilter
              ? favoriteFilters.find((f) => f.value === selectedFavoriteFilter)?.item_ids?.join(',')
              : undefined,
            ...dataFilterAdvance
          },
          headers: {
            Authorization: `Bearer ${user?.accessToken}`
          },
          responseType: 'blob'
        }
      );
      const dateKey = moment().format('YYYYMMDD.HHmmss');
      const type = response.headers['content-type'];
      const blob = new Blob([response.data], { type: type });
      const link1 = document.createElement('a');
      link1.href = window.URL.createObjectURL(blob);
      link1.setAttribute('download', `Thong_ke_tieu_thu_theo ngày_${dateKey}.xlsx`);
      document.body.appendChild(link1);
      link1.click();
      setIsExportModalOpen(false);
    } catch (err) {
      showError({ message: 'Xuất báo cáo thất bại!' });
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenExportModal = () => {
    setExportDateRange([
      rangeDateString?.[0] ? dayjs(rangeDateString[0]) : defaultStatisticsDate,
      rangeDateString?.[1] ? dayjs(rangeDateString[1]) : defaultStatisticsDate
    ]);
    setExportRangeDateString([
      rangeDateString?.[0] || defaultStatisticsDate.format('YYYY-MM-DD'),
      rangeDateString?.[1] || defaultStatisticsDate.format('YYYY-MM-DD')
    ]);
    setIsExportModalOpen(true);
  };
  const [loadingSync, setLoadingSync] = useState(false);
  const handleSyncButtonClick = async () => {
    try {
      setLoadingSync(true);
      const res = await apiSyncTimeData();
      // message.success('Cập nhật thành công !');
      fetchData();
    } catch (e) {
      console.log(e);
      message.error('Có lỗi xảy ra !');
    } finally {
      setLoadingSync(false);
    }
  };

  const handleRowClick = async (row) => {
    if (!row?.diffirenceOfTheFlowWaterMeter?.length) {
      showError('Khách hàng không có dữ liệu ngày hôm nay!');
      return;
    }
    setSelectedRowKey(row?.stt);
    setSelectedRow(row);
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth'
    });
  };

  const dataCharts = useMemo(() => {
    if (!selectedRow) {
      return [
        {
          name: 'Tiêu thụ giờ',
          data: [],
          type: 'bar'
        }
      ];
    }

    const hourlyConsumption =
      selectedRow?.diffirenceOfTheFlowWaterMeter?.map((value) => (isNumber(value) ? value : 0)) ||
      [];

    const cumulativeReading = buildMeterReadingSeries(selectedRow, hourlyConsumption);

    return [
      {
        name: 'Tiêu thụ giờ',
        data: hourlyConsumption,
        type: 'bar'
      },
      {
        name: 'Chỉ số đồng hồ',
        data: cumulativeReading,
        type: 'line'
      }
    ];
  }, [selectedRow]);

  useEffect(() => {
    handleSyncButtonClick();
  }, []);

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <Typography.Title level={4}>
            Thống kê tiêu thụ theo ngày nhiều khách hàng
          </Typography.Title>
        </Col>
        {dailyConsumption.export && (
          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleOpenExportModal}
              loading={loading}
              style={{
                backgroundColor: 'var(--action-export-bg)',
                borderColor: 'var(--action-export-border)',
                color: 'var(--action-export-text)',
                height: '36px',
                fontWeight: '500'
              }}>
              Xuất báo cáo
            </Button>
          </Col>
        )}
      </Row>
      <Card bodyStyle={{ padding: '8px' }}>
        <Row gutter={[8, 8]}>
          {/* <Col md={4} xs={6}>
            <Input
              value={serialNo}
              onChange={handleSerialNoChange}
              placeholder="Tìm kiếm số serial..."
              style={{ width: '100%', marginBottom: '16px', height: '36px' }}
            />
          </Col> */}
          <Col md={1} xs={1}>
            <Button
              icon={<FilterOutlined />}
              onClick={openDrawer}
              style={{
                backgroundColor: 'var(--surface-3)',
                color: 'var(--muted)',
                border: 'none',
                width: '100%'
              }}
            />
          </Col>
          <Col md={5} xs={8}>
            <Select
              placeholder="Chọn Bộ lọc theo dõi..."
              value={selectedFavoriteFilter}
              onChange={(value) => {
                setSelectedFavoriteFilter(value);
                setCurrentPage(1);
              }}
              loading={loadingFavoriteFilters}
              allowClear
              options={favoriteFilters}
              style={{ width: '100%', height: '36px' }}
            />
          </Col>
          <Col md={4} xs={6}>
            <Input
              style={{ height: '36px', width: '100%' }}
              size="small"
              placeholder="Tìm kiếm theo mã tên KH..."
              value={search}
              onChange={handleSearchChange}
              onPressEnter={() => {
                setCurrentPage(1);
                setFlagSearch(!flagSearch);
              }}
            />
          </Col>
          <Col md={4} xs={6}>
            <DatePicker
              value={selectedDate}
              onChange={onChangeDate}
              style={{ width: '100%', height: '36px' }}
              format="YYYY-MM-DD"
              placeholder="Chọn ngày"
              defaultPickerValue={defaultStatisticsDate}
            />
          </Col>
          {/* <Col md={4} xs={6}>
            <SelectCommunity value={selectedCommunity} onChange={setSelectedCommunity} />
          </Col> */}

          <Col md={4} xs={6}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => {
                if (!selectedDate) {
                  message.error('Vui lòng chọn ngày!');
                  return;
                }
                setCurrentPage(1);
                setFlagSearch(!flagSearch);
              }}
              style={{
                height: '36px',
                backgroundColor: '#0284c7',
                borderColor: '#0284c7'
              }}>
              Tìm kiếm
            </Button>
          </Col>
          <ConsumptionCard
            rangeDateString={rangeDateString}
            dataFilterAdvance={dataFilterAdvance}
            flagSearch={flagSearch}
            keyWord={search}
            serialNo={serialNo}
            selectedFavoriteFilter={selectedFavoriteFilter}
            favoriteFilters={favoriteFilters}
          />
        </Row>
      </Card>
      <Table
        bordered
        // size="middle"
        loading={loadingFetch}
        dataSource={data || []}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          onChange: handleChangePage,
          onShowSizeChange: handleChangePageSize,
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        rowClassName={(record) => (selectedRowKey === record.stt ? 'ant-table-row-selected' : '')}
        onRow={(record) => ({
          onClick: () => handleRowClick(record), // Click handler for rows
          style: { cursor: 'pointer' }
        })}
        style={{
          borderRadius: 8,
          thead: { backgroundColor: '#e2e8f0', color: '#64748b' },
          fontSize: '30px'
        }}
        scroll={{ x: 'max-content', y: 400 }}
      />
      {selectedRowKey && (
        <ChartCard
          style={{ marginTop: 16 }}
          title="Tiêu thụ các giờ trong ngày"
          subtitle={`${selectedRow?.code} - ${selectedRow?.name} (${selectedRow?.denseData_startCollectionTime})`}>
          <Chart
            options={consumptionWithMeterOptions({
              labels: Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'))
            })}
            type="bar"
            series={dataCharts}
            height={380}
          />
        </ChartCard>
      )}
      <FilterAdvanceDrawer
        setCurrentPage={setCurrentPage}
        isOpen={isOpen}
        onDrawerClose={onDrawerClose}
        dataFilterAdvance={dataFilterAdvance}
        setDataFilterAdvance={setDataFilterAdvance}
        flagSearch={flagSearch}
        setFlagSearch={setFlagSearch}
      />
      <Modal
        title="Chọn khoảng ngày xuất báo cáo"
        open={isExportModalOpen}
        onOk={handleExportExcelButtonClick}
        onCancel={() => setIsExportModalOpen(false)}
        okText="Xuất báo cáo"
        cancelText="Hủy"
        confirmLoading={loading}>
        <RangePicker
          value={exportDateRange}
          onChange={onChangeExportDateRange}
          style={{ width: '100%' }}
          format="YYYY-MM-DD"
          placeholder={['Từ ngày', 'Đến ngày']}
          defaultPickerValue={[dayjs().subtract(0, 'month'), dayjs()]}
        />
      </Modal>
    </div>
  );
}
